import { ENTITYTYPE } from '../data/constants';
import { insertMessage } from '../services/messageService';
import { Message } from '../types/Message';
import { getCurrentESTTime, getEstDateInString } from '../utils/dateHelper';
import useApi from './useApi';

export const useSendMessage = () => {
    const { executeApi } = useApi();

    const getMessageFormData = (message: Message, attachments: File[] = []): FormData => {
        const formData = new FormData();

        const messageJson = {
            messageId: message.messageId,
            fromUserId: message.fromUserId,
            cacheFromName: message.cacheFromName ?? "",
            toUserIds: message.toUserIds,
            toCacheNames: message.toCacheNames ?? [],
            recipientTypes: message.recipientTypes ?? [],
            createdOn: message.createdOn,
            createdBy: message.createdBy,
            sentOn: message.sentOn,
            sentBy: message.sentBy,
            cacheCreatedByName: message.cacheCreatedByName ?? "",
            cacheSentByName: message.cacheSentByName ?? "",
            markedAsRead: message.markedAsRead,
            messageSubject: message.messageSubject ?? "",
            messageBody: message.messageBody ?? "",
            messagePriority: message.messagePriority,
            conversationId: message.conversationId ?? null,
            replyToMessageId: message.replyToMessageId ?? null,
            linkToEntityId: message.linkToEntityId ?? null,
            linkToEntityType: message.linkToEntityType ?? "",
            conversationCount: message.conversationCount,
            redirectLink: message.redirectLink ?? "",
            description: message.description ?? "",
            totalRecords: message.totalRecords,
        };
        
        formData.append("messageJson", JSON.stringify(messageJson));

        if (attachments) {
            attachments.forEach((file) => {
                formData.append("formFiles", file);
            });
        }

        return formData;
    };


    const sendMessage = async (
        fromId: number,
        fromName: string,
        toIds: number[],
        toNames: string[],
        recipientTypes: string[],
        subject: string,
        messageBody: string,
        markedAsRead: boolean,
        messagePriority: number,
        sentOn: Date | null,
        attachments?: File[],
        conversationId?: number | null,
        replyToMessageId?: number | null
    ): Promise<boolean> => {
        const currentTime = sentOn ? getEstDateInString(sentOn) : getCurrentESTTime();

        const message: Message = {
            messageId: 0,
            fromUserId: fromId,
            cacheFromName: fromName,
            toUserIds: toIds,
            toCacheNames: toNames,
            recipientTypes: recipientTypes,
            createdOn: currentTime,
            createdBy: fromId,
            // Bug 72470: only a Schedule Send carries a send time. An immediate send leaves it
            // out so the API stamps the server's EST time - this PC's clock running ahead made the
            // message look scheduled, so no MessageAlert (unread badge) fired for the recipient.
            sentOn: sentOn ? currentTime : undefined,
            sentBy: fromId,
            cacheCreatedByName: fromName,
            cacheSentByName: fromName,
            markedAsRead: markedAsRead,
            messageSubject: subject,
            messageBody,
            messagePriority: messagePriority,
            conversationId: conversationId,
            replyToMessageId: replyToMessageId,
            linkToEntityId: null,
            linkToEntityType: ENTITYTYPE.EMAIL,
            conversationCount: 0,
            redirectLink: null,
            description: null,
            totalRecords: 0,
        };

        var formData = getMessageFormData(message, attachments);
        const result = await executeApi<FormData, Message>(insertMessage, formData);
        return result !== null;
    };

    return { sendMessage };
};
